import { isEmpty } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";

import { copy } from "shared/assets";
import { FormBuilder, Map, Message } from "shared/components";
import {
  DEFAULT_COORDINATES,
  DEFAULT_FOCUS_ZOOM,
  DEFAULT_ZOOM,
} from "shared/components/Map/utils/constants";
import { Spacer } from "shared/layout";
import { GeocodingService } from "shared/services";
import { IAddress, ICoordinate, IStatus } from "shared/types";
import { useScrollIntoView } from "Venues/utils";
import { buttons, getFields } from "./formData";
import { MapContainer } from "./styles";

interface AddressFormProps {
  address: IAddress | null;
  setAddress: (address: IAddress | null) => void;
  submitAddress?: (address: IAddress | null) => void;
  blurAddress?: (address: IAddress | null) => void;
  defaultValues?: IAddress | Record<string, never>;
  tempAddress: IAddress | null;
  setTempAddress: (address: IAddress | null) => void;
}

export const GooglePlacesAddressForm: React.FC<AddressFormProps> = ({
  address,
  setAddress,
  submitAddress,
  blurAddress,
  defaultValues,
  tempAddress,
  setTempAddress,
}) => {
  const [message, setMessage] = useState<string | null>();
  const [mapZoom, setMapZoom] = useState<number | null>(
    !isEmpty(defaultValues) ? DEFAULT_FOCUS_ZOOM : DEFAULT_ZOOM
  );
  const [loadingGeocoding, setLoadingGeocoding] = useState(false);

  const checkAddressValid = useCallback((address: Partial<IAddress>): boolean => {
    if (!address) return false;

    const { addressLine1, city, zip, country } = address;

    return (
      (addressLine1 &&
        addressLine1 !== "" &&
        city &&
        city !== "" &&
        zip &&
        zip !== "" &&
        country &&
        country !== "") ||
      false
    );
  }, []);

  const addressIsValid = useMemo(
    () => checkAddressValid(tempAddress as Partial<IAddress>),
    [tempAddress, checkAddressValid]
  );

  const { ref, scrollIntoView } = useScrollIntoView();

  const coordinates: ICoordinate | undefined = useMemo(() => {
    if (!address) return DEFAULT_COORDINATES;

    const { longitude, latitude } = address || {};

    setMapZoom(DEFAULT_FOCUS_ZOOM);

    if (typeof longitude === "number" && typeof latitude === "number") {
      return {
        latitude,
        longitude,
      };
    }
  }, [address]);

  const handleChange = useCallback(
    ({ value, name }: { value: string; name: string }) => {
      const newAddress = { ...tempAddress, [name]: value };

      console.log("newAddress", newAddress);

      setTempAddress(newAddress as IAddress);

      if (checkAddressValid(newAddress)) {
        setMessage(copy.createVenue.messages.confirmAddress);
      } else {
        setMessage(null);
      }
    },
    [tempAddress, setTempAddress, checkAddressValid]
  );

  const fields = useMemo(() => getFields(handleChange), [handleChange]);

  const handleSubmit = async (address: IAddress) => {
    setMessage(null);
    setLoadingGeocoding(true);

    let coordinates: ICoordinate = DEFAULT_COORDINATES;
    try {
      coordinates = await GeocodingService.get(address);
      setMapZoom(DEFAULT_FOCUS_ZOOM);
    } catch (error) {
      setMessage(copy.createVenue.messages.unknownAddress);
      setMapZoom(DEFAULT_ZOOM);
    }
    setLoadingGeocoding(false);
    scrollIntoView(true);
    handleSubmitAddress({ ...address, ...coordinates });
  };

  const handleSubmitAddress = (address: Partial<IAddress>) => {
    setAddress(address as IAddress);
    submitAddress && submitAddress(address as IAddress);
  };

  const updateCoordinates = (coordinates: ICoordinate) => {
    if (address) {
      setMapZoom(null);
      handleSubmitAddress({ ...tempAddress, ...coordinates });
    }
  };

  return (
    <React.Fragment>
      <Spacer size="l">
        <FormBuilder
          fields={fields}
          buttons={buttons}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onBlur={blurAddress}
          style={{
            gridTemplateColumns: "1fr 1fr",
            mobile: { gridTemplateColumns: "1fr" },
          }}
          isSubmitting={loadingGeocoding}
          preventSubmit={!addressIsValid}
        />

        {!!message && <Message message={message} status={IStatus.warning} />}
        <MapContainer ref={ref}>
          <Map
            coordinates={coordinates}
            draggable={{
              setCoordinates: updateCoordinates,
              tooltip: copy.createVenue.messages.dragMarker,
            }}
            zoom={mapZoom}
          />
        </MapContainer>
      </Spacer>
    </React.Fragment>
  );
};
